/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Outlet } from 'react-router-dom'
import { AuthProvider } from '@/components/auth/AuthProvider'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AppLoader } from '@/components/feedback/AppLoader'
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary'
import { ToastProvider } from '@/components/feedback/Toast'
import { GlobalShortcuts } from '@/components/layout/GlobalShortcuts'
import { PaperTexture } from '@/components/layout/PaperTexture'

// Lazy load pages for code splitting
const SignupPage = lazy(() => import('@/pages/SignupPage'))
const SignupVerifyPage = lazy(() => import('@/pages/SignupVerifyPage'))
const SignupConfirmPage = lazy(() => import('@/pages/SignupConfirmPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'))
const TermsOfServicePage = lazy(() => import('@/pages/TermsOfServicePage'))
const LandingPage = lazy(() => import('@/pages/LandingPage'))
const SubscribePage = lazy(() => import('@/pages/SubscribePage'))
const RootPage = lazy(() => import('@/pages/RootPage'))

const ReviewPage = lazy(() => import('@/pages/ReviewPage'))
const CreatePage = lazy(() => import('@/pages/CreatePage'))
const QueuePage = lazy(() => import('@/pages/QueuePage'))
const CardsPage = lazy(() => import('@/pages/CardsPage'))
const CardDetailPage = lazy(() => import('@/pages/CardDetailPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

/**
 * Root layout wrapper with AuthProvider, Suspense, and ErrorBoundary
 */
function RootLayout(): ReactNode {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <PaperTexture />
          <Suspense fallback={<AppLoader />}>
            <Outlet />
          </Suspense>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

/**
 * Public routes layout (no auth required)
 */
function PublicLayout(): ReactNode {
  return <Outlet />
}

/**
 * Protected routes layout (auth required)
 * Wraps children with ProtectedRoute for authentication check
 * Also includes global keyboard shortcuts
 */
function ProtectedLayout(): ReactNode {
  return (
    <ProtectedRoute>
      <GlobalShortcuts />
      <Outlet />
    </ProtectedRoute>
  )
}

/**
 * Application router configuration
 */
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public routes
      {
        element: <PublicLayout />,
        children: [
          {
            path: '/',
            element: <RootPage />,
          },
          {
            path: '/signup',
            element: <SignupPage />,
          },
          {
            path: '/signup/verify',
            element: <SignupVerifyPage />,
          },
          {
            path: '/signup/confirm',
            element: <SignupConfirmPage />,
          },
          {
            path: '/login',
            element: <LoginPage />,
          },
          {
            path: '/forgot-password',
            element: <ForgotPasswordPage />,
          },
          {
            path: '/reset-password',
            element: <ResetPasswordPage />,
          },
          {
            path: '/privacy',
            element: <PrivacyPolicyPage />,
          },
          {
            path: '/terms',
            element: <TermsOfServicePage />,
          },
          {
            path: '/welcome',
            element: <LandingPage />,
          },
          {
            path: '/subscribe',
            element: <SubscribePage />,
          },
        ],
      },
      // Protected routes
      {
        element: <ProtectedLayout />,
        children: [
          {
            path: '/create',
            element: <CreatePage />,
          },
          {
            path: '/review/:deckId',
            element: <ReviewPage />,
          },
          {
            path: '/queue',
            element: <QueuePage />,
          },
          {
            path: '/cards',
            element: <CardsPage />,
          },
          {
            path: '/cards/:id',
            element: <CardDetailPage />,
          },
          {
            path: '/settings',
            element: <SettingsPage />,
          },
        ],
      },
      // 404 Not Found page for unmatched routes
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
